import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { UserIdentity, UserIdentityApi } from '@venecia/apis/auth';
import { ApiResponse } from '@venecia/apis/base';

import { StorageService } from 'app/shared/services';
import { GlobalConstants } from 'app/shared/constants';
import { OAuthService } from 'app/shared/services/oauth.service';
import { Bearer } from '@venecia/apis/auth/model/bearer.model';
import { CurrentUser } from '@venecia/models';
import { JwtUtils } from '@venecia/utils';

/**
 * Auth facade for SPA.
 *
 * Responsibilities:
 * - Provide a valid access token (refreshing when possible).
 * - Bootstrap the current user profile.
 *
 * Notes:
 * - Token acquisition is handled via OAuth Authorization Code + PKCE (browser redirect).
 * - This facade never starts redirects; guards/pages handle flow decisions.
 */
@Injectable({ providedIn: 'root' })
export class AuthFacadeService {
    private readonly oauthService = inject(OAuthService);
    private readonly profileApi = inject(UserIdentityApi);
    private readonly storage = inject(StorageService);

    hasValidAccessToken(): boolean {
        const bearer = this.oauthService.getBearer();
        return JwtUtils.isJwtValid(bearer?.access_token);
    }

    /**
     * Returns a valid access token.
     * - Uses stored bearer if access_token is valid
     * - Otherwise refreshes (if refresh_token is present)
     */
    getValidAccessToken$(): Observable<string> {
        const bearer = this.oauthService.getBearer();

        if (JwtUtils.isJwtValid(bearer?.access_token)) {
            return of(bearer!.access_token);
        }

        if (bearer?.refresh_token) {
            return this.oauthService.refresh$().pipe(
                map((b: Bearer) => b.access_token)
            );
        }

        return throwError(() => new Error('Missing OAuth bearer'));
    }

    /**
     * Loads current profile after authentication.
     */
    bootstrap$(): Observable<UserIdentity> {
        return this.getValidAccessToken$()
            .pipe(
                switchMap(() => this.profileApi.me().pipe(take(1))),
                map((resp: ApiResponse<UserIdentity>) => resp.body),
                tap((user: UserIdentity) => {
                    const currentUser = {
                        initials: user?.['last-name']?.charAt(0) + "" + user?.['first-name']?.charAt(0),
                        fullName: user['last-name'] + " " + user['first-name'],
                        avatarUrl: user['avatar-url'],
                        phone: user['phone'],
                        email: user['username'],
                        profileName: user.profiles[0]?.name,
                        roles: user.roles.map(role => role.code)
                    } as CurrentUser
                    this.storage.set<CurrentUser>(GlobalConstants.CURRENT_USER_KEY, currentUser);
                })
            );
    }
}
